import { Link, Outlet, useLocation } from '@tanstack/react-router';

import {
  Bell, Building2, CreditCard, HelpCircle, MapPin, Menu, MessageSquareText, ShieldCheck, Store, Tag, User,
  Users,
} from 'lucide-react';

import { Button, cn } from '@src/components/ui';
import { useActiveOrg, usePermissions, useSidebar } from '@src/context/hooks';

import { useRoleLabel } from './bits';
import MobileHub from './MobileHub';


/** Settings tabs. `manage` = Owner/Manager only; `owner` = Owner only;
 * `write` = Owner/Manager/Lister (read-capable roles, i.e. everyone but Viewer).
 * Notifications is available to every role; Security (sessions + danger zone)
 * is Owner only. */
const TABS = [
  { to: '/app/settings/profile', label: 'Profile', icon: User, manage: false, owner: false },
  { to: '/app/settings/business', label: 'Business', icon: Building2, manage: true, owner: false },
  { to: '/app/settings/team', label: 'Team', icon: Users, manage: true, owner: false },
  { to: '/app/settings/channels', label: 'Channels', icon: Tag, manage: true, owner: false },
  { to: '/app/settings/storefront', label: 'Storefront', icon: Store, manage: true, owner: false },
  {
    to: '/app/settings/storefront-faq',
    label: 'Storefront FAQ',
    icon: HelpCircle,
    manage: false,
    owner: false,
    write: true,
  },
  { to: '/app/settings/locations', label: 'Locations', icon: MapPin, manage: true, owner: false },
  { to: '/app/settings/billing', label: 'Billing', icon: CreditCard, manage: true, owner: true },
  { to: '/app/settings/saved-replies', label: 'Saved Replies', icon: MessageSquareText, manage: true, owner: false },
  { to: '/app/settings/notifications', label: 'Notifications', icon: Bell, manage: false, owner: false },
  { to: '/app/settings/security', label: 'Security', icon: ShieldCheck, manage: false, owner: true },
];


function TabNav({ tabs, pathname }) {
  return (
    <nav
      className={cn(
        'hidden w-[220px] shrink-0 flex-col gap-0.5 border-r border-hairline p-3 lg:flex',
      )}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        // The bare /app/settings index resolves to Profile, so light it too.
        const active = pathname === tab.to
          || (tab.to === '/app/settings/profile' && pathname === '/app/settings');
        return (
          <Link
            key={tab.to}
            className={cn(
              'flex h-9 items-center gap-2.5 rounded-lg px-[11px] text-table',
              active
                ? 'bg-primary-soft font-semibold text-ink'
                : 'font-medium text-ink-muted hover:bg-surface-2',
            )}
            to={tab.to}
          >
            <Icon className={cn('size-4', active ? 'text-primary' : 'text-ink-subtle')} />
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

TabNav.displayName = 'TabNav';


/** Settings layout (#144): two-column shell on desktop (tab rail + panel),
 * a grouped hub list on mobile. Tabs are role-filtered; direct access to a
 * restricted tab is redirected inside each page component. */
function SettingsShell() {
  const sidebar = useSidebar();
  const location = useLocation();
  const roleLabel = useRoleLabel();
  const { membership } = useActiveOrg();
  const { canManageOrg, canWrite, isOwner } = usePermissions();

  const tabs = TABS.filter(
    (tab) => (!tab.manage || canManageOrg) && (!tab.owner || isOwner) && (!tab.write || canWrite),
  );
  const atHub = location.pathname === '/app/settings' || location.pathname === '/app/settings/';

  if (!membership) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Desktop top bar */}
      <div className="hidden h-16 items-center border-b border-hairline px-1 lg:flex">
        <h1 className="text-xl font-semibold tracking-[-0.4px] text-ink">Settings</h1>
      </div>

      {/* Mobile top bar — only the hub carries the menu button; sub-pages render
          their own header with a back affordance. */}
      {atHub
        ? (
          <div className="flex h-14 items-center gap-3 border-b border-hairline px-1 lg:hidden">
            <Button
              size="icon"
              type="button"
              variant="ghost"
              aria-label="Menu"
              className="size-auto bg-transparent p-0 hover:bg-transparent"
              onClick={() => sidebar.toggle()}
            >
              <Menu className="size-[22px] text-ink" />
            </Button>

            <span className="text-[17px] font-semibold tracking-[-0.3px] text-ink">Settings</span>
          </div>
        )
        : null}

      <div className="flex flex-1 lg:mt-0">
        <TabNav tabs={tabs} pathname={location.pathname} />

        {/* Mobile: the bare index is the hub list; sub-routes render full-screen. */}
        {atHub
          ? (
            <div className="flex-1 lg:hidden">
              <MobileHub membership={membership} roleLabel={roleLabel} />
            </div>
          )
          : null}

        <div className={cn('min-w-0 flex-1', atHub && 'hidden lg:block')}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

SettingsShell.displayName = 'SettingsShell';
export default SettingsShell;
